/**
 * Event `email` uses the same address string as the PostHog person property.
 * Placeholder suffixes omit the key. `is_test_account` stays on the person.
 */

const SYNTHETIC_EMAIL_SUFFIXES = [
  '@memara.dev',
  '@unknown.memara.dev',
  '@integration.memara.dev',
] as const;

const EVENTS_WITHOUT_EMAIL = new Set([
  '$exception',
  '$pageview',
  '$pageleave',
  '$autocapture',
  '$rageclick',
  '$dead_click',
  '$web_vitals',
  '$$heatmap',
  'error_boundary_shown',
  'critical_error',
  'client_handled_operation_failed',
  'api_error',
  'page_load',
  'page_viewed',
  'search_performance',
  'login_failed',
  'embedding_fallback_used',
]);

let currentProductEmail: string | null = null;

export function isSyntheticProductEmail(
  email: string | null | undefined,
): boolean {
  if (!email || !email.trim()) {
    return true;
  }
  return SYNTHETIC_EMAIL_SUFFIXES.some((suffix) => email.endsWith(suffix));
}

/** `{ email }` for a real address, otherwise an empty object. */
export function productEventEmail(
  email: string | null | undefined,
): { email?: string } {
  if (!email || isSyntheticProductEmail(email)) {
    return {};
  }
  return { email };
}

export function setCurrentProductEmail(
  email: string | null | undefined,
): void {
  if (!email || isSyntheticProductEmail(email)) {
    currentProductEmail = null;
    return;
  }
  currentProductEmail = email;
}

export function clearCurrentProductEmail(): void {
  currentProductEmail = null;
}

export function currentProductEventEmail(): { email?: string } {
  return productEventEmail(currentProductEmail);
}

export function eventSkipsProductEmail(eventName: string | undefined): boolean {
  if (!eventName) {
    return true;
  }
  if (EVENTS_WITHOUT_EMAIL.has(eventName)) {
    return true;
  }
  if (eventName.startsWith('$') || eventName.startsWith('rate_limit_')) {
    return true;
  }
  if (eventName.includes('performance')) {
    return true;
  }
  return false;
}

export function withProductEmail<T extends object>(
  properties: T,
  email?: string | null,
): T & { email?: string } {
  const extra = productEventEmail(email);
  if (!extra.email) {
    if (!('email' in properties)) {
      return properties;
    }
    const { email: _removed, ...rest } = properties as T & { email?: string };
    return rest as T & { email?: string };
  }
  return { ...properties, email: extra.email };
}

export function attachProductEmail<
  T extends {
    event?: string;
    properties?: Record<string, unknown> | null;
  },
>(event: T | null): T | null {
  if (!event || eventSkipsProductEmail(event.event)) {
    return event;
  }
  const extra = currentProductEventEmail();
  if (!extra.email) {
    return event;
  }
  const properties: Record<string, unknown> = { ...(event.properties || {}) };
  const existing = properties.email;
  if (typeof existing === 'string' && !isSyntheticProductEmail(existing)) {
    return event;
  }
  if ('email' in properties) {
    delete properties.email;
  }
  properties.email = extra.email;
  return { ...event, properties };
}
